// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * @fileoverview Seeds the four built-in org roles (owner, manager,
 * contributor, viewer) into a given org. This seeder is idempotent
 * and is safe to call on every application startup.
 *
 * Call order: this seeder must complete before
 * {@link migrateTeamMembersToRoleMemberships} is invoked, because
 * the migration resolves role names against the seeded records.
 */

import { createId } from '../utils/id'
import type { RoleModel } from '../models/Role'
import type { TeamMemberRole } from '../types/teamMember'

const BUILT_IN_ROLES: ReadonlyArray<{ name: TeamMemberRole; title: string; description: string }> = [
  { name: 'owner', title: 'Owner', description: 'Full administrative access' },
  { name: 'manager', title: 'Manager', description: 'Manage team members and settings' },
  { name: 'contributor', title: 'Contributor', description: 'Create and edit content' },
  { name: 'viewer', title: 'Viewer', description: 'Read-only access' },
]

/** RxDB-compatible collection interface required by {@link seedBuiltInRoles}. */
interface RolesCollectionLike {
  findOne(query: { selector: Record<string, unknown> }): {
    exec(): Promise<{ id: string; name: string } | null>
  }
  insert(doc: RoleModel): Promise<unknown>
}

/**
 * Seeds the four built-in org roles (owner, manager, contributor, viewer) if they
 * do not already exist for the given org. Safe to call multiple times — existing
 * roles are detected via a compound selector on `name + orgId + builtIn`.
 *
 * @param rolesCollection - RxDB (or compatible) collection for roles
 * @param orgId - The org to seed roles for
 * @returns Counts of created and skipped roles
 */
export async function seedBuiltInRoles(
  rolesCollection: RolesCollectionLike,
  orgId: string
): Promise<{ created: number; skipped: number }> {
  let created = 0
  let skipped = 0

  for (const def of BUILT_IN_ROLES) {
    const existing = await rolesCollection
      .findOne({ selector: { name: def.name, orgId, builtIn: true } })
      .exec()

    if (existing) {
      skipped++
      continue
    }

    const now = new Date().toISOString()
    await rolesCollection.insert({
      id: createId('roles'),
      name: def.name,
      title: def.title,
      description: def.description,
      orgId,
      scopeType: '',
      scopeId: '',
      builtIn: true,
      archived: false,
      createdAt: now,
      updatedAt: now,
    })
    created++
  }

  return { created, skipped }
}
